import React, { useEffect, useState } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { Material, IRoute } from '@sp-types/index';
import { HistoryChart } from './HistoryChart';
import { ChartViewToolbar } from '../../ProductOverview/ProductOverviewChart/ChartViewToolbar';
import { ChartViewOptions } from '../../ProductOverview/ProductOverviewChart/ProductChartUtils';
import { ToggleChips } from '../../../Dashboard/dashboard-components/ToggleChips';
import { default as rebar_sig1 } from './data/rebar-sig1-data.json';
import { default as rebar_sig2 } from './data/rebar-sig2-data.json';
import { calcRouteColor, getStartDate, PricingSkeleton, sliceFromDate } from './MaterialDetailUtils';
import { CloseButton } from '@UI-Elements/Buttons/CloseButton';

interface Props {
  material: Material;
  isView: boolean;
  onEdit: () => void;
  routes?: IRoute[];
  selectedRoute?: IRoute;
}
export const MaterialPricing: React.FC<Props> = ({ material, isView, onEdit, routes, selectedRoute }) => {
  const [selectedViewOption, setSelectedViewOption] = React.useState<number>(24);
  const [sigma, setSigma] = React.useState<number>(1);
  const [visiblePrices, setVisiblePrices] = React.useState<any[]>(
    sliceFromDate(sigma === 1 ? rebar_sig1 : rebar_sig2, getStartDate(selectedViewOption)),
  );
  const [refreshState, setRefreshState] = useState(1);

  useEffect(() => {
    setRefreshState(refreshState + 1);
  }, [isView]);

  const [selectedPoint, setSelectedPoint] = React.useState<{ dataPoint: any; index: number }>({
    dataPoint: null,
    index: 0,
  });

  React.useEffect(() => {
    if (selectedViewOption) {
      setVisiblePrices(sliceFromDate(sigma === 1 ? rebar_sig1 : rebar_sig2, getStartDate(selectedViewOption)));
    }
  }, [selectedViewOption, sigma]);

  // bad name
  const [routesMap, setRoutesMap] = React.useState<Record<string, string>>({} as Record<string, string>);
  React.useEffect(() => {
    if (routes?.length) {
      const routeMap = routes.reduce(
        (acc, itm, i) => ({ ...acc, [itm.id]: 'route' + (i + 1) }),
        {} as Record<string, string>,
      );

      setRoutesMap(routeMap);
    }
  }, [routes]);
  const getRouteIndex = (route?: IRoute): string => (route && routesMap[route.id]) || 'route1';

  const chips = [
    { key: 'σ1', value: 1 },
    { key: 'σ2', value: 2 },
  ];

  const onDataClick = (index: number) => {
    setSelectedPoint({ dataPoint: visiblePrices[index], index });
  };

  return visiblePrices?.length && selectedViewOption ? (
    <div className="flex flex-col p-3 gap-3 h-full">
      <div className="flex flex-grow-0 justify-between gap-4">
        <h4>Pricing</h4>
        <div>
          <ToggleChips chips={chips} selected={sigma} setSelected={setSigma} />
        </div>
        <div className="flex flex-1">&nbsp;</div>
        <ChartViewToolbar
          viewOptions={ChartViewOptions}
          selectedValue={selectedViewOption}
          onSelectionChange={setSelectedViewOption}
        />
        <CloseButton onClick={onEdit}>{isView && <EditOutlined />}</CloseButton>
      </div>

      <div className="flex flex-row flex-1 grow h-full gap-4 justify-between   ">
        <div className={`${isView ? 'flex-1' : 'flex-1 '} `}>
          <HistoryChart
            key={refreshState}
            prices={visiblePrices}
            showRoute={getRouteIndex(selectedRoute)}
            onDataClick={onDataClick}
          />
        </div>
        <KeyPoints
          getRouteIndex={getRouteIndex}
          material={material}
          selectedPoint={selectedPoint}
          selectedRoute={selectedRoute}
        />
      </div>
    </div>
  ) : (
    <PricingSkeleton />
  );
};

interface KeyPointsProps {
  selectedPoint: {
    dataPoint: any;
    index: number;
  };
  material: Material;
  getRouteIndex: (route?: IRoute) => string;
  selectedRoute?: IRoute;
}

const KeyPoints: React.FC<KeyPointsProps> = ({ material, selectedPoint, getRouteIndex, selectedRoute }) => {
  return (
    <div className="flex flex-col text-sm w-40 gap-2">
      <div className="flex flex-col">
        <p className="text-red-200 p-0">Current price</p>
        <p className="p-0">{material.currentPrice}</p>
      </div>
      <p className="text-red-200">Focus on</p>
      <div className="flex flex-col ps-2 border-l-4 border-l-blue-600">
        <p className="text-gray-450">For date</p>
        <p>{selectedPoint.dataPoint?.date}</p>
      </div>
      <div className="flex flex-col ps-2 border-l-4 border-l-purple-300">
        <p className="text-gray-450">Price</p>
        <p>{selectedPoint.dataPoint?.price}</p>
      </div>
      <p></p>
      <div
        className="flex flex-col ps-2 border-l-4"
        style={{ borderLeftColor: calcRouteColor(getRouteIndex(selectedRoute)) }}
      >
        <p className="text-gray-450">Route price</p>
        <p>{selectedPoint.dataPoint?.[getRouteIndex(selectedRoute)]}</p>
      </div>
    </div>
  );
};
